/**
 * D331's dismissal record — a device-local preference (D255, minted inside D331 rather than beside
 * it), in `pwa/updateAlerts.ts`'s shape: every read and write wrapped so a missing or refused store
 * reads as *not dismissed*, and published so the one already-mounted consumer (`HomeNoticePortal`)
 * re-renders on a tap rather than waiting for an unrelated one.
 *
 * **`id`-keyed, and the record holds only the LAST dismissed id.** The wire carries at most one
 * notice at a time (`ClientConfigDocument.notice`), so there is never a second live id to
 * remember — "a tapped-away notice does not return, and a new `id` is a new notice" (R9 §4.5) is
 * exactly *the current notice's `id` differs from the one stored here*, which is what
 * `HomeNoticePortal` compares rather than keeping a set.
 *
 * `docs/product/device-cache.md`'s row for this record says where it is not: not personal data,
 * not cleared on sign-out, account switch or erasure. The stage segment in the key is what keeps
 * one stage's dismissal from silencing another stage's differently-`id`'d notice — the same reason
 * `cache.ts` keys the document itself by stage.
 */
import { STAGE } from '../auth/config'

const dismissedKey = (stage: string): string => `selogate.${stage}.client-config.dismissed`

/**
 * **Never throws** — a private-mode browser throws on `getItem` rather than returning `null`
 * (`auth/credentials.ts`'s rule, restated here for the same reason `readUpdateAlerts` restates
 * it): a refused store must read as *nothing was ever dismissed*, never as a boot failure.
 */
function readDismissedNoticeId(stage: string = STAGE): string | undefined {
  try {
    const raw = globalThis.localStorage?.getItem(dismissedKey(stage))
    return typeof raw === 'string' && raw.length > 0 ? raw : undefined
  } catch {
    return undefined
  }
}

let dismissedId: string | undefined = readDismissedNoticeId()
const listeners = new Set<() => void>()

/** `useSyncExternalStore`'s subscribe half. */
export function subscribeToDismissedNotice(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** `useSyncExternalStore`'s snapshot half — the cached value, never a re-read of storage. */
export function getDismissedNoticeId(): string | undefined {
  return dismissedId
}

/** Records `id` as dismissed. A tap the resident is looking at is published even if the write throws. */
export function dismissNotice(id: string, stage: string = STAGE): void {
  try {
    globalThis.localStorage?.setItem(dismissedKey(stage), id)
  } catch {
    /* ignore — see readDismissedNoticeId */
  }
  if (id === dismissedId) return
  dismissedId = id
  for (const listener of listeners) listener()
}

/** Test seam. */
export function __resetDismissedNoticeForTests(stage: string = STAGE): void {
  try {
    globalThis.localStorage?.removeItem(dismissedKey(stage))
  } catch {
    /* ignore */
  }
  dismissedId = readDismissedNoticeId(stage)
  for (const listener of listeners) listener()
}
